import React, { useEffect, useState } from "react";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import SubChecklistCommentForm from "~/components/Forms/SubChecklistCommentForm";
import Back from "~/components/Icons/arrow-prev2";
import Button from "~/shared/ui/Fields/Button";
import { getFormatDate } from "~/libs/getFormatDate";
import { useChecklistsPage } from "~/pages/Checklists/hooks/useChecklistsPage";
import {
  getChecklistDetailColumns,
  getCommentsColumns,
} from "~/pages/Checklists/model/tableAdapter";
import { useCheckListCommentGetterLazyQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { TableV2 } from "~/shared/ui/TableV2";

import ActionPlan from "./ActionPlan";
import ChecklistHeader from "./ChecklistHeader";
import HeaderInfo from "./HeaderInfo";

import s from "~/pages/Checklists/checklists.less";

const Checklist = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const [commentsData, setCommentsData] = useState([]);
  const [activeTab, setActiveTab] = useState("checklist");
  const params = useParams<{ id: string }>();
  const { id } = params;
  const history = useHistory();
  const goBack = () => {
    history.replace("/pkd/checklists");
  };

  const { checklists } = useChecklistsPage({ detailId: id });

  const [getCheckListComment, getCheckListCommentState] =
    useCheckListCommentGetterLazyQuery({
      fetchPolicy: "no-cache",
    });

  const getCheckListCommentHandler = () => {
    getCheckListComment({
      variables: {
        checkListId: id,
      },
    });
  };

  useEffect(() => {
    if (hasPermission("pkd.getCheckListComments")) {
      getCheckListCommentHandler();
    }
  }, [id, hasPermission]);

  useEffect(() => {
    if (getCheckListCommentState.loading)
      getCheckListCommentState.data = undefined;
    if (getCheckListCommentState.data) {
      const requestData = get(
        getCheckListCommentState.data,
        "CheckListCommentGetter.getCheckListComments",
      );
      if (requestData && hasPermission("pkd.getCheckListComments")) {
        setCommentsData(Array.isArray(requestData) ? requestData : []);
      }
    }
  }, [getCheckListCommentState.loading]);

  const resultMathRound = (actualPoints, maxPoints) => {
    return Math.round(100 * (actualPoints / maxPoints));
  };

  const {
    name: documentName,
    DealerName: dealerName,
    externalCode,
    createdAt: rawCreatedAt,
    actualPoints,
    maxPoints,
    result,
    id: checklistId,
    Indicators,
  } = checklists[0] || {};

  const createdAt = rawCreatedAt ? getFormatDate(rawCreatedAt, false) : "";
  const updatedIndicators = Indicators?.map((indicator) => ({
    ...indicator,
    percentage: indicator?.name,
    result: Number.isNaN(
      resultMathRound(indicator.actualPoints, indicator.maxPoints),
    )
      ? "0"
      : resultMathRound(indicator.actualPoints, indicator.maxPoints),
  }));

  return (
    <div className={s.wrapperTables}>
      <Button
        txtBtn={"Назад"}
        onClick={goBack}
        small
        customClassName={s.add_bth}
      >
        <Back />
      </Button>
      <HeaderInfo
        id={checklistId}
        createdAt={createdAt}
        documentName={documentName}
        dealerName={dealerName}
      />
      <div className={s.btns}>
        <Button
          txtBtn={"Чек-лист"}
          onClick={() => setActiveTab("checklist")}
          withMargin
          orange={activeTab === "checklist"}
        />
        <Button
          txtBtn={"План действий"}
          onClick={() => setActiveTab("actionPlan")}
          withMargin
          orange={activeTab === "actionPlan"}
        />
      </div>
      {activeTab === "checklist" ? (
        <>
          <ChecklistHeader
            actualPoints={actualPoints}
            maxPoints={maxPoints}
            result={result}
            id={checklistId}
          />
          <div className={s.wrapperChecklist}>
            <TableV2
              columns={getChecklistDetailColumns(t)}
              dataSource={updatedIndicators ?? []}
              rowKey={(_, index) => String(index)}
            />
          </div>
          {hasPermission("pkd.getCheckListComments") && (
            <div className={s.wrapperChecklist}>
              <div className={s.titleChecklist}>
                {t(`comment.comment_title`)}
              </div>
              <TableV2
                columns={getCommentsColumns(t)}
                dataSource={commentsData}
                rowKey="id"
              />
            </div>
          )}
          {hasPermission("pkd.createCheckListComment") && (
            <SubChecklistCommentForm checklistId={checklistId} />
          )}
        </>
      ) : (
        <ActionPlan checklists={checklists} />
      )}
    </div>
  );
};

export default Checklist;
